// src/components/admin/SettingsManagement.js 


import React, { useEffect, useState, useCallback } from "react";
import api from "../../api/api";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { XMarkIcon } from "@heroicons/react/24/solid";
import { 
    Facebook, Instagram, Twitter, Youtube, Linkedin, 
    Github, Globe, Mail, Phone 
} from 'lucide-react';

// ============================================================
// PART 0 - COMPONENTES AUXILIARES E UI GENÉRICOS
// (Mantidos do seu código original)
// ============================================================

const BACKEND_URL = "https://imagensdaface.com.br";
const DEFAULT_LOGO_SIZE = 80;

const generateUniqueId = () => `widget-${Date.now()}-${Math.floor(Math.random() * 1000)}`;

const getFullLogo = (url) => {
    if (!url || typeof url !== "string") return "";
    if (
        url.startsWith("http") ||
        url.startsWith("blob:") ||
        url.startsWith("data:") ||
        url.startsWith("//")
    ) {
        return url;
    }
    return `${BACKEND_URL}/${url.replace(/^\/+/, "")}`;
};

const getLogoEffectClass = (effect) => {
  switch (effect) {
    case "round-shadow":
      return "site-logo round-shadow";

    case "neon-glow":
      return "site-logo neon-glow";

    case "border-glow":
      return "site-logo border-glow";

    case "grayscale":
      return "site-logo grayscale";

    default:
      return "site-logo";
  }
};

const getEffectStyles = (effect, neonColor = "#ff00ff") => {
    const validNeonColor = /^#([0-9A-F]{3}){1,2}$/i.test(neonColor) ? neonColor : "#ff00ff";

    switch (effect) {
        case "rain":
            return {
                boxShadow: "inset 0 0 10px rgba(0, 100, 255, 0.6)",
                border: "2px solid rgba(0, 100, 255, 0.4)",
                backgroundBlendMode: "overlay",
                backgroundImage:
                    "linear-gradient(rgba(0,0,0,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(0,0,0,0.1) 1px, transparent 1px)",
                backgroundSize: "4px 4px",
            };
        case "stars":
            return {
                boxShadow: "inset 0 0 15px rgba(255, 255, 0, 0.8)",
                backgroundImage:
                    "radial-gradient(circle, #ffffcc 1px, transparent 1px), radial-gradient(circle, #fff 1px, transparent 1px)",
                backgroundSize: "20px 20px, 40px 40px",
                backgroundPosition: "0 0, 10px 10px",
            };
        case "neon":
            return {
                boxShadow: `0 0 15px ${validNeonColor}, 0 0 20px ${validNeonColor}`,
                border: `1px solid ${validNeonColor}`,
            };
        case "glass":
            return {
                backdropFilter: "blur(5px)",
                backgroundColor: "rgba(255, 255, 255, 0.1)",
                border: "1px solid rgba(255, 255, 255, 0.2)",
            };
        case "dots":
            return {
                backgroundImage: `radial-gradient(${validNeonColor} 1px, transparent 1px)`,
                backgroundSize: "10px 10px",
                opacity: 0.9,
            };
        case "wave":
            return {
                backgroundImage: `repeating-linear-gradient(45deg, ${validNeonColor} 0%, transparent 10%), repeating-linear-gradient(-45deg, ${validNeonColor} 0%, transparent 10%)`,
                backgroundSize: "20px 20px",
                backgroundBlendMode: "overlay",
            };
        default:
            return {};
    }
};

const widgetTemplate = (type = "text") => {
    const base = { id: generateUniqueId(), enabled: true };
    switch (type) {
        case "address":
            return { ...base, type: "address", title: "Endereço", content: "Rua X, 123\nCidade - UF" };
        case "links":
            return { ...base, type: "links", title: "Links", content: "Política | /politica\nTermos | /termo" };
        case "social":
            return { ...base, type: "social", title: "Redes Sociais", content: "Instagram | https://instagram.com/conta | instagram\nFacebook | https://facebook.com/conta | facebook" };
        case "map":
            return { ...base, type: "map", title: "Localização", content: "Rua Exemplo, 123 - Cidade\n" };
        case "html":
            return { ...base, type: "html", title: "HTML", content: "<strong>Texto</strong>" };
        default:
            return { ...base, type: "text", title: "Texto", content: "Texto do widget" };
    }
};

function flattenZones(zones) {
    return [
        ...(zones.zone1 || []),
        ...(zones.zone2 || []),
        ...(zones.zone3 || []),
        ...(zones.zone4 || []),
    ];
}

// -----------------------------------------------------
// COMPONENTES DE UI GENÉRICOS (Mantidos)
// -----------------------------------------------------
const MiniModal = ({ show, title, message }) => {
    if (!show) return null;
    return (
        <div className="fixed top-4 right-4 z-[9999] bg-white border border-gray-300 rounded-lg shadow-xl p-4 max-w-sm transition-opacity duration-300 ease-in-out">
            <h4 className="font-bold text-lg text-indigo-700 mb-1">{title}</h4>
            <p className="text-sm text-gray-600">{message}</p>
        </div>
    );
};

const InputModal = ({ show, keyName, placeholder, onSubmit, onClose }) => {
    const [inputValue, setInputValue] = useState("");

    useEffect(() => {
        if (show) setInputValue("");
    }, [show]);

    if (!show) return null;

    const handleSubmit = () => {
        if (onSubmit) onSubmit(inputValue);
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[10000] flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-md">
                <h4 className="font-bold text-xl mb-4 text-indigo-700">Inserir URL Externa para: {keyName}</h4>
                <p className="text-sm text-gray-600 mb-4">Cole a URL completa do arquivo (imagem, ícone, etc.) aqui.</p>
                <input
                    type="text"
                    className="w-full border border-gray-300 rounded-lg p-2 mb-4 focus:ring-indigo-500 focus:border-indigo-500 transition duration-150"
                    placeholder={placeholder}
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                />
                <div className="flex justify-end gap-3">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-sm font-medium border border-gray-300 rounded-lg hover:bg-gray-100 transition"
                    >
                        Cancelar
                    </button>
                    <button
                        onClick={handleSubmit}
                        className="px-4 py-2 text-sm font-medium bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition"
                    >
                        Aplicar URL
                    </button>
                </div>
            </div>
        </div>
    );
};

const Toggle = ({ checked, onChange, label, disabled = false }) => (
    <div className="flex items-center justify-between mb-2">
        <div className="text-sm font-medium text-gray-700">{label}</div>
        <button
            type="button"
            onClick={() => !disabled && onChange(!checked)}
            disabled={disabled}
            className={`h-6 w-12 rounded-full p-0.5 flex items-center transition-colors duration-300 ease-in-out ${
                checked ? "bg-indigo-600" : "bg-gray-300"
            } ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
        >
            <div
                className={`w-5 h-5 bg-white rounded-full shadow transform transition-transform duration-300 ease-in-out ${
                    checked ? "translate-x-6" : "translate-x-0"
                }`}
            />
        </button>
    </div>
);

const RichTextEditor = ({ value, onChange, placeholder, rows = 4 }) => (
    <textarea
        rows={rows}
        className="w-full border rounded p-1 text-xs resize-y font-mono"
        value={value}
        onChange={onChange}
        placeholder={placeholder}
    />
);

// Seu SmtpSettingsModal COMPLETO
const SmtpSettingsModal = ({ show, settings, onSave, onTest, onDelete, onClose, isSaving }) => {
    const [localSettings, setLocalSettings] = useState({ ...settings });
    const [error, setError] = useState(null);

    useEffect(() => {
        setLocalSettings({ ...settings });
        setError(null);
    }, [settings, show]);

    if (!show) return null;

    const handleUpdate = (k, v) => setLocalSettings(p => ({ ...p, [k]: v }));

    const handleSaveLocal = async () => {
        if (!localSettings.SMTP_HOST || !localSettings.SMTP_USER) {
            setError("O Host e o Usuário SMTP são campos obrigatórios.");
            return;
        }
        setError(null);
        await onSave(localSettings);
    };

    const handleTestLocal = async () => {
        if (!localSettings.SMTP_HOST || !localSettings.SMTP_USER || !localSettings.SMTP_PORT) {
            setError("Preencha HOST, PORTA e USUÁRIO para testar.");
            return;
        }
        setError(null);
        await onTest(localSettings);
    };

    const isConfigured = !!settings.SMTP_HOST && !!settings.SMTP_USER && settings.SMTP_PASS === "************";
    const passwordPlaceholder = isConfigured ? "Preencha APENAS para trocar a senha" : "********";
    const isPasswordPlaceholder = localSettings.SMTP_PASS === "************";


    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[10000] flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
                <h4 className="font-bold text-2xl mb-4 text-indigo-700">
                    {isConfigured ? '⚙️ Editar Configuração SMTP' : '📧 Nova Configuração SMTP'}
                </h4>
                <p className="text-sm text-gray-600 mb-6">Insira as credenciais do seu servidor de e-mail SMTP. A senha só precisa ser preenchida se você quiser alterá-la.</p>

                {error && <div className="p-3 mb-4 bg-red-100 border border-red-400 text-red-700 rounded-lg text-sm">{error}</div>}

                <div className="space-y-4">
                    {/* HOST e PORTA */}
                    <div className="flex gap-4">
                        <div className="flex-1">
                            <label className="block text-sm font-medium mb-1 text-gray-700">Servidor SMTP (HOST) <span className="text-red-500">*</span></label>
                            <input
                                className="w-full border border-gray-300 rounded-lg p-2 focus:ring-indigo-500 focus:border-indigo-500 transition duration-150"
                                placeholder="smtp.seudominio.com"
                                value={localSettings.SMTP_HOST || ""}
                                onChange={(e) => handleUpdate("SMTP_HOST", e.target.value)}
                            />
                        </div>
                        <div className="w-24">
                            <label className="block text-sm font-medium mb-1 text-gray-700">Porta <span className="text-red-500">*</span></label>
                            <input
                                type="number"
                                className="w-full border border-gray-300 rounded-lg p-2 text-center focus:ring-indigo-500 focus:border-indigo-500 transition duration-150"
                                placeholder="587"
                                value={localSettings.SMTP_PORT || ""}
                                onChange={(e) => handleUpdate("SMTP_PORT", Number(e.target.value))}
                            />
                        </div>
                    </div>

                    {/* USUÁRIO e SENHA */}
                    <div className="flex gap-4">
                        <div className="flex-1">
                            <label className="block text-sm font-medium mb-1 text-gray-700">Usuário (Endereço de E-mail) <span className="text-red-500">*</span></label>
                            <input
                                className="w-full border border-gray-300 rounded-lg p-2 focus:ring-indigo-500 focus:border-indigo-500 transition duration-150"
                                placeholder="no-reply@seudominio.com"
                                value={localSettings.SMTP_USER || ""}
                                onChange={(e) => handleUpdate("SMTP_USER", e.target.value)}
                            />
                        </div>
                        <div className="flex-1">
                            <label className="block text-sm font-medium mb-1 text-gray-700">Senha</label>
                            <input
                                type="password"
                                className={`w-full border border-gray-300 rounded-lg p-2 focus:ring-yellow-500 focus:border-yellow-500 transition duration-150 ${isConfigured && isPasswordPlaceholder ? 'bg-yellow-50' : ''}`}
                                placeholder={passwordPlaceholder}
                                value={localSettings.SMTP_PASS || ""}
                                onChange={(e) => handleUpdate("SMTP_PASS", e.target.value)}
                            />
                            <p className="text-xs text-gray-500 mt-1">Se configurado, deixe em branco para manter a senha atual.</p>
                        </div>
                    </div>

                    {/* NOME DE REMETENTE */}
                    <div>
                        <label className="block text-sm font-medium mb-1 text-gray-700">Nome de Remetente (FROM)</label>
                        <input
                            className="w-full border border-gray-300 rounded-lg p-2 focus:ring-indigo-500 focus:border-indigo-500 transition duration-150"
                            placeholder={settings.SITE_NAME || "Nome da sua Empresa"}
                            value={localSettings.EMAIL_FROM_NAME || ""}
                            onChange={(e) => handleUpdate("EMAIL_FROM_NAME", e.target.value)}
                        />
                    </div>
                      
                    {/* CONEXÃO SEGURA */}
                    <div className="pt-3 border-t border-gray-200">
                        <Toggle 
                            checked={!!localSettings.SMTP_SECURE} 
                            onChange={(v) => handleUpdate("SMTP_SECURE", v)} 
                            label="Usar Conexão Segura (SSL/TLS)" 
                        />
                        <p className="text-xs text-gray-500 -mt-1">Use SSL/TLS. Recomendado (obrigatório se a porta for 465).</p>
                    </div>
                </div>

                {/* BOTÕES DE AÇÃO */}
                <div className="flex justify-between items-center mt-6 pt-4 border-t border-gray-200">
                    <div className="flex gap-2">
                        {isConfigured && (
                            <button
                                onClick={onDelete}
                                className="px-4 py-2 text-sm font-medium bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition"
                                disabled={isSaving}
                            >
                                🗑️ Excluir Conta
                            </button>
                        )}
                    </div>
                      
                    <div className="flex gap-3">
                        <button
                            onClick={handleTestLocal}
                            className="px-4 py-2 text-sm font-medium bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition"
                            disabled={isSaving || !localSettings.SMTP_HOST || !localSettings.SMTP_USER || (isConfigured && isPasswordPlaceholder)}
                        >
                            🧪 Testar Conexão
                        </button>
                        <button
                            onClick={onClose}
                            className="px-4 py-2 text-sm font-medium border border-gray-300 rounded-lg hover:bg-gray-100 transition"
                            disabled={isSaving}
                        >
                            Cancelar
                        </button>
                        <button
                            onClick={handleSaveLocal}
                            className="px-4 py-2 text-sm font-medium bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition"
                            disabled={isSaving}
                        >
                            {isSaving ? 'Salvando...' : '💾 Salvar Configurações'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};


// ============================================================
// NOVO COMPONENTE: CertificateControlCard (Controle de Certificado)
// ============================================================

function CertificateControlCard({ setNotification }) {
    const [courses, setCourses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [savingId, setSavingId] = useState(null);

    // Função para carregar os cursos com o status de certificado
    const loadCourses = useCallback(async () => {
        try {
            // Assumimos que a rota /courses retorna o campo certificates_enabled (0 ou 1)
           const res = await api.get("/courses/admin/certificate-settings");
           setCourses(res.data.courses || []);

        } catch (err) {
            console.error("Erro ao carregar lista de cursos para certificado:", err);
            setNotification?.({ show: true, type: "error", message: "Falha ao carregar cursos para controle de certificado." });
        } finally {
            setLoading(false);
        }
    }, [setNotification]);

    useEffect(() => {
        loadCourses();
    }, [loadCourses]);

    const handleToggle = async (courseId, currentStatus) => {
        setSavingId(courseId);
        // Inverte o status: 1 (Ativo) -> 0 (Inativo), 0 (Inativo) -> 1 (Ativo)
        const newStatus = currentStatus === 1 ? 0 : 1;
        
        try {
            // Rota PATCH que deve ser criada no seu backend para atualizar APENAS o status do certificado
            await api.patch(`/courses/${courseId}/certificate-status`, {
                certificates_enabled: newStatus
            });

            // Atualiza o estado local
            setCourses(prev => prev.map(c => 
                c.id === courseId ? { ...c, certificates_enabled: newStatus } : c
            ));

            setNotification?.({ 
                show: true, 
                type: "success", 
                message: newStatus === 1 
                    ? `Certificado ativado para o curso ${courseId}.` 
                    : `Certificado desativado para o curso ${courseId}.` 
            });

        } catch (err) {
            console.error("Erro ao atualizar status:", err);
            setNotification?.({ show: true, type: "error", message: "Falha ao salvar status. Verifique se a rota PATCH está configurada no backend para: /courses/{id}/certificate-status" });
        } finally {
            setSavingId(null);
        }
    };

    return (
        <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200 h-fit">
            <h3 className="font-bold text-xl mb-4 text-indigo-600">🏆 Controle de Emissão de Certificados</h3>
            <p className="text-sm text-gray-500 mb-4">Gerencie quais cursos podem emitir certificados ao serem concluídos. (Desativado = não gera certificado)</p>
            
            <div className="text-xs font-semibold text-gray-700 grid grid-cols-12 border-b pb-2 mb-2">
                <span className="col-span-7">Curso</span>
                <span className="col-span-5 text-center">Permissão</span>
            </div>

            {loading ? (
                <div className="text-center py-6 text-gray-500">Carregando cursos...</div>
            ) : courses.length === 0 ? (
                <div className="text-center py-6 text-gray-500">Nenhum curso encontrado.</div>
            ) : (
                <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2">
                    {courses.map(c => (
                        <div key={c.id} className="grid grid-cols-12 items-center text-sm border-b border-gray-100 pb-2">
                            <span className="col-span-7 truncate text-gray-800 pr-2">{c.title}</span>
                            <div className="col-span-5 flex items-center justify-center">
                                <span className={`text-xs mr-2 font-medium ${c.certificates_enabled === 1 ? 'text-green-600' : 'text-red-600'}`}>
                                    {c.certificates_enabled === 1 ? 'Ativo' : 'Inativo'}
                                </span>
                                <Toggle
                                    checked={c.certificates_enabled === 1}
                                    onChange={() => handleToggle(c.id, c.certificates_enabled)}
                                    disabled={savingId === c.id}
                                />
                                {savingId === c.id && <span className="text-xs text-indigo-600 ml-2">Salvando...</span>}
                            </div>
                        </div>
                    ))}
                </div>
            )}
            
            <p className="text-xs text-gray-500 mt-4 pt-4 border-t">
                * **Status Ativo:** Permite ao aluno gerar o certificado ao atingir 100% de conclusão.
            </p>
        </div>
    );
}

// (PARTE 2/3)

function SettingsManagement({ setNotification }) {
    const [loading, setLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
      
    // 💡 E-mail
    const [isEmailConfigured, setIsEmailConfigured] = useState(false);
    const [isSmtpModalOpen, setIsSmtpModalOpen] = useState(false); 

    // 💡 Google OAuth
    const [isGoogleConnected, setIsGoogleConnected] = useState(false);
    const [isGoogleCheckingStatus, setIsGoogleCheckingStatus] = useState(true);
    const [googleInfo, setGoogleInfo] = useState({}); // { name, email, avatar, connected_at }

    // 💡 Controla a aba ativa no Card de Conexões
    const [activeConnectionTab, setActiveConnectionTab] = useState('smtp');


  const [settings, setSettings] = useState({
  SITE_NAME: "",
  SITE_LOGO: "",
  SITE_LOGO_SIZE: DEFAULT_LOGO_SIZE,

  // ✅ DEFAULT CORRETA (sem backend aqui)
  SITE_LOGO_SHADOW_COLOR: "#000000",

  SHOW_LOGO_TYPE: "logo_and_name",
  SITE_LOGO_EFFECT: "none",

  HOME_SLIDER_ENABLED: false,
  MAINTENANCE_MODE: false,

  // === ESTADOS DE E-MAIL ===
  SMTP_HOST: "",
  SMTP_PORT: 587,
  SMTP_USER: "",
  SMTP_PASS: "",
  SMTP_SECURE: true,
  EMAIL_FROM_NAME: "",

  HEADER_BG_COLOR: "",
  HEADER_BG_GRADIENT: "",
  HEADER_EFFECT: "none",
  HEADER_TEXT_COLOR: "#ffffff",
  HEADER_NEON_COLOR: "#ff00ff",

  FOOTER_ENABLED: true,
  FOOTER_MODE: "builder",
  FOOTER_TEXT: "",
  FOOTER_LINKS: "",
  FOOTER_ZONES: { zone1: [], zone2: [], zone3: [], zone4: [] },
  FOOTER_BG_COLOR: "",
  FOOTER_BG_GRADIENT: "",
  FOOTER_TEXT_COLOR: "#ffffff",
  FOOTER_EFFECT: "none",
  FOOTER_NEON_COLOR: "#ff00ff",

  MENU_ITEMS: "",

  CONTACT_BUTTON_ENABLED: false,
  CONTACT_BUTTON_TEXT: "Fale Conosco",
  CONTACT_BUTTON_COLOR: "#4f46e5",
  CONTACT_BUTTON_ICON: "",
  CONTACT_BUTTON_LAYOUT: "pill",
  CONTACT_BUTTON_SIZE: "medium",
  CONTACT_BUTTON_POSITION: "bottom-right",
  CONTACT_BUTTON_PAGES: "*",
  CONTACT_MODAL_CONTENT: "[form id=1]",
});

    // Mini Modal (success/warn/info)
    const [miniModal, setMiniModal] = useState({ show: false, title: "", message: "" });
    const openMiniModal = (title, message, autoCloseMs = 4000) => {
        setMiniModal({ show: true, title, message });
        if (autoCloseMs) setTimeout(() => setMiniModal({ show: false, title: "", message: "" }), autoCloseMs);
    };

    // Input Modal (for external URL inputs)
    const [inputModal, setInputModal] = useState({ show: false, key: "", placeholder: "", onSubmit: null });
    const openInputModal = (key, placeholder = "https://dominio.com/imagem.png") => {
        setInputModal({ show: true, key, placeholder, onSubmit: (value) => {
            if (!value || value.trim() === "") {
                openMiniModal("Aviso", "Nenhuma URL foi informada.");
                setInputModal({ show: false, key: "", placeholder: "", onSubmit: null });
                return;
            }
            update(key, value.trim());
            openMiniModal("Sucesso", "URL externa aplicada. Salve para persistir.");
            setInputModal({ show: false, key: "", placeholder: "", onSubmit: null });
        } });
    };
    const closeInputModal = () => setInputModal({ show: false, key: "", placeholder: "", onSubmit: null });

    const update = (k, v) => setSettings((p) => ({ ...p, [k]: v }));

    const generateGoogleMapUrl = (address) => {
        if (!address) return "";
        const encodedAddress = encodeURIComponent(address.trim());
        // Simulação de URL de mapa embeddable
        return `https://maps.google.com/maps?q=${encodedAddress}&output=embed&z=14`;
    };

    const loadEmailSettings = async () => {
        try {
            const res = await api.get("/email/settings");
            const data = res.data || {};
              
            const configured = !!data.SMTP_HOST && !!data.SMTP_USER;
            setIsEmailConfigured(configured);
              
            return {
                ...data,
                // Se configurado, retorna um placeholder para a senha
                SMTP_PASS: configured ? "************" : "", 
            };
        } catch (err) {
            console.error("Erro ao carregar Email Settings:", err);
            setIsEmailConfigured(false);
            return {};
        }
    };
      
    const checkGoogleStatus = async () => {
        setIsGoogleCheckingStatus(true);
        try {
            const res = await api.get("/api/v1/auth/oauth/google/status");
            const data = res.data?.data || res.data || {}; 
              
            if (res.status === 200 && data.connected) {
                setIsGoogleConnected(true);
                setGoogleInfo(data.user || {});
                setActiveConnectionTab('google');
            } else {
                setIsGoogleConnected(false);
                setGoogleInfo({});
            }
        } catch (err) {
            console.error("Erro ao verificar status do Google OAuth:", err);
            setIsGoogleConnected(false);
            setGoogleInfo({});
        } finally {
            setIsGoogleCheckingStatus(false);
        }
    };


   useEffect(() => {
    let mounted = true;
    const load = async () => {
        try {
            setLoading(true);
            // 1. Carrega Email e Google Status primeiro
            const emailSettings = await loadEmailSettings();
            await checkGoogleStatus();

            // 2. Busca configurações globais do Banco
            const globalRes = await api.get("/global/settings");
            const s = globalRes.data?.settings || globalRes.data || {};
            
            if (!mounted) return;

            // 3. Processamento robusto do Footer
            let footerZones = { zone1: [], zone2: [], zone3: [], zone4: [] };
            try {
                if (s.FOOTER_ZONES) {
                    footerZones = typeof s.FOOTER_ZONES === 'string' ? JSON.parse(s.FOOTER_ZONES) : s.FOOTER_ZONES;
                }
            } catch (e) { console.error("Erro parse Footer Zones", e); }

            // 4. ATUALIZAÇÃO DO ESTADO (Prioridade total ao que vem do Banco 's')
            setSettings({
                // Identidade
                SITE_NAME: s.SITE_NAME ?? "",
                SITE_LOGO: s.SITE_LOGO ?? "",
                SITE_LOGO_SIZE: Number(s.SITE_LOGO_SIZE || 80),
                SITE_LOGO_EFFECT: s.SITE_LOGO_EFFECT ?? "none",
                SITE_LOGO_SHADOW_COLOR: s.SITE_LOGO_SHADOW_COLOR ?? "#000000",
                SHOW_LOGO_TYPE: s.SHOW_LOGO_TYPE ?? "logo_and_name",

                // Flags (Tratamento para 0/1 ou boolean)
                HOME_SLIDER_ENABLED: Boolean(Number(s.HOME_SLIDER_ENABLED)),
                MAINTENANCE_MODE: Boolean(Number(s.MAINTENANCE_MODE)),

                // Header
                HEADER_BG_COLOR: s.HEADER_BG_COLOR ?? "",
                HEADER_BG_GRADIENT: s.HEADER_BG_GRADIENT ?? "",
                HEADER_EFFECT: s.HEADER_EFFECT ?? "none",
                HEADER_TEXT_COLOR: s.HEADER_TEXT_COLOR ?? "#ffffff",
                HEADER_NEON_COLOR: s.HEADER_NEON_COLOR ?? "#ff00ff",

                // Footer
                FOOTER_ENABLED: Boolean(Number(s.FOOTER_ENABLED)),
                FOOTER_MODE: s.FOOTER_MODE ?? "simple",
                FOOTER_TEXT: s.FOOTER_TEXT ?? "",
                FOOTER_LINKS: s.FOOTER_LINKS ?? "",
                FOOTER_ZONES: footerZones,
                FOOTER_BG_COLOR: s.FOOTER_BG_COLOR ?? "",
                FOOTER_BG_GRADIENT: s.FOOTER_BG_GRADIENT ?? "",
                FOOTER_TEXT_COLOR: s.FOOTER_TEXT_COLOR ?? "#ffffff",
                FOOTER_EFFECT: s.FOOTER_EFFECT ?? "none",
                FOOTER_NEON_COLOR: s.FOOTER_NEON_COLOR ?? "#ff00ff",

                // Contato
                CONTACT_BUTTON_ENABLED: Boolean(Number(s.CONTACT_BUTTON_ENABLED)),
                CONTACT_BUTTON_TEXT: s.CONTACT_BUTTON_TEXT ?? "Fale Conosco",
                CONTACT_BUTTON_COLOR: s.CONTACT_BUTTON_COLOR ?? "#4f46e5",
                CONTACT_BUTTON_ICON: s.CONTACT_BUTTON_ICON ?? "",
                CONTACT_BUTTON_LAYOUT: s.CONTACT_BUTTON_LAYOUT ?? "pill",
                CONTACT_BUTTON_SIZE: s.CONTACT_BUTTON_SIZE ?? "medium",
                CONTACT_BUTTON_POSITION: s.CONTACT_BUTTON_POSITION ?? "bottom-right",
                CONTACT_BUTTON_PAGES: s.CONTACT_BUTTON_PAGES ?? "*",
                CONTACT_MODAL_CONTENT: s.CONTACT_MODAL_CONTENT ?? "",

                // Email (SMTP vindos da rota de email)
                SMTP_HOST: emailSettings.SMTP_HOST || "",
                SMTP_PORT: Number(emailSettings.SMTP_PORT || 587),
                SMTP_USER: emailSettings.SMTP_USER || "",
                SMTP_PASS: emailSettings.SMTP_PASS || "",
                SMTP_SECURE: Boolean(emailSettings.SMTP_SECURE),
                EMAIL_FROM_NAME: emailSettings.EMAIL_FROM_NAME || "",
                // Pagamentos
                EFI_CLIENT_ID: "",
                EFI_CLIENT_SECRET: "",
                EFI_CERTIFICATE_NAME: "",
                EFI_SANDBOX: false,
                                
                                MENU_ITEMS: s.MENU_ITEMS ?? "",
            });

        } catch (err) {
            console.error("Erro ao carregar settings:", err);
        } finally {
            if (mounted) setLoading(false);
        }
    };
    load();
    return () => { mounted = false; };
}, []);


    // =========================
    // PART 2 - UPLOADS, WIDGET MANAGEMENT, DND & GOOGLE OAUTH HANDLERS
    // (A maioria da lógica de handlers permanece inalterada)
    // =========================

    const handleLogoUpload = async (file) => {
        if (!file) return;
        try {
            const fm = new FormData();
            fm.append("file", file);
            setNotification?.({ show: true, type: "info", message: "Enviando logo..." });
            const res = await api.post("/upload", fm, { headers: { "Content-Type": "multipart/form-data" } });
            const url = (res && res.data && (res.data.file?.url || res.data.url || res.data.filepath)) || "";
            if (!url) throw new Error("Resposta inválida do upload");
            update("SITE_LOGO", url);
            openMiniModal("Sucesso", "Logo carregado. Salve para persistir.");
        } catch (err) {
            console.error("Erro upload logo:", err);
            setNotification?.({ show: true, type: "error", message: "Erro ao enviar logo." });
        }
    };

    const handleInsertExternalUrl = (key) => {
        openInputModal(key, "https://dominio.com/imagem.png");
    };
      
    const handleContactIconUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const fm = new FormData();
        fm.append("file", file);
        setNotification?.({ show: true, type: "info", message: "Enviando ícone..." });
        try {
            const res = await api.post("/upload", fm, { headers: { "Content-Type": "multipart/form-data" } });
            const url = res.data?.file?.url || res.data?.url || res.data?.filepath;
            if (url) {
                update("CONTACT_BUTTON_ICON", url);
                openMiniModal("Sucesso", "Ícone carregado.");
            }
        } catch (error) {
            setNotification?.({ show: true, type: "error", message: "Erro ao enviar ícone." });
        }
    };

    // FOOTER WIDGET HELPERS (DND)
    const addWidget = (type = 'text', zone = 'zone1') => {
        const template = widgetTemplate(type);
        const zones = { ...(settings.FOOTER_ZONES || { zone1: [], zone2: [], zone3: [], zone4: [] }) };
        zones[zone] = [...(zones[zone] || []), template];
        update('FOOTER_ZONES', zones);
    };

    const updateWidget = (zone, i, patch) => {
        const zones = { ...(settings.FOOTER_ZONES || {}) };
        const arr = [...(zones[zone] || [])];
        arr[i] = { ...(arr[i] || {}), ...patch };
        zones[zone] = arr;
        update('FOOTER_ZONES', zones);
    };

    const removeWidget = (zone, i) => {
        const zones = { ...(settings.FOOTER_ZONES || {}) };
        const arr = [...(zones[zone] || [])];
        arr.splice(i, 1);
        zones[zone] = arr;
        update('FOOTER_ZONES', zones);
    };

    const onDragEnd = (result) => {
        const { source, destination } = result;
        if (!destination) return;
        const srcZone = source.droppableId;
        const dstZone = destination.droppableId;
        const srcIdx = source.index;
        const dstIdx = destination.index;

        const zones = { ...(settings.FOOTER_ZONES || { zone1: [], zone2: [], zone3: [], zone4: [] }) };

        const srcArr = [...(zones[srcZone] || [])];
        const dstArr = srcZone === dstZone ? srcArr : [...(zones[dstZone] || [])];

        const [moved] = srcArr.splice(srcIdx, 1);
        dstArr.splice(dstIdx, 0, moved);

        if (srcZone !== dstZone) {
            zones[srcZone] = srcArr;
            zones[dstZone] = dstArr;
        } else {
            zones[srcZone] = srcArr;
        }

        update('FOOTER_ZONES', zones);
    };
    // FIM (DND)

    // Handlers para Google OAuth (MANTIDOS)
    const handleGoogleConnect = () => {
        const windowFeatures = "width=600,height=600,scrollbars=yes,resizable=yes";
        const oauthWindow = window.open(
            "https://imagensdaface.com.br/api/v1/auth/oauth/google/connect",
            "_blank",
            windowFeatures
        );
          
        const timer = setInterval(() => {
            if (oauthWindow.closed) {
                clearInterval(timer);
                checkGoogleStatus(); 
                setNotification({ show: true, type: "info", message: "Verificando status de conexão com Google..." });
            }
        }, 1000);
    };

    const handleGoogleDisconnect = async () => {
        if (!window.confirm("Tem certeza que deseja desconectar a conta do Google?")) return;

        try {
            setNotification({ show: true, type: "info", message: "Desconectando conta do Google..." });
            await api.get("/api/v1/auth/oauth/google/disconnect");
            await checkGoogleStatus(); 
            setNotification({ show: true, type: "success", message: "Conta do Google desconectada com sucesso." });
        } catch (err) {
            console.error("Erro ao desconectar Google OAuth:", err);
            setNotification({ show: true, type: "error", message: "Erro ao desconectar conta do Google." });
        }
    };

    const handleTestGoogleApi = async () => {
        try {
            setNotification({ show: true, type: "info", message: "Testando a API do Google (Post)..." });
            const res = await api.post("/api/v1/auth/oauth/google/test"); 
            setNotification({ show: true, type: "success", message: res.data?.message || "Teste de API do Google bem-sucedido!" });
        } catch (err) {
            console.error("Erro ao testar Google API:", err);
            const errorMessage = err.response?.data?.message || `Erro: ${err.message}`;
            setNotification({ show: true, type: "error", message: `Falha no Teste: ${errorMessage}` });
        }
    };
      
    // Handlers para SMTP (MANTIDOS)
    const handleSaveEmailSettings = async (emailPayloadFromModal) => {
        setIsSaving(true);

        try {
            const isPasswordPlaceholder = emailPayloadFromModal.SMTP_PASS === "************";
            const smtpPassValue = isPasswordPlaceholder ? "" : emailPayloadFromModal.SMTP_PASS;

            const emailPayload = {
                SMTP_HOST: emailPayloadFromModal.SMTP_HOST,
                SMTP_PORT: Number(emailPayloadFromModal.SMTP_PORT || 587),
                SMTP_USER: emailPayloadFromModal.SMTP_USER,
                SMTP_PASS: smtpPassValue, 
                SMTP_SECURE: !!emailPayloadFromModal.SMTP_SECURE,
                EMAIL_FROM_NAME: emailPayloadFromModal.EMAIL_FROM_NAME,
            };

            await api.put('/email/settings', emailPayload);

            setSettings(p => ({ ...p, ...emailPayload, SMTP_PASS: "************" }));
            setIsEmailConfigured(true);
            setIsSmtpModalOpen(false); 
            setNotification({ show: true, type: 'success', message: 'Configurações de E-mail salvas com sucesso!' });

        } catch (err) {
            console.error('Erro ao salvar configurações de E-MAIL:', err);
            setNotification({ show: true, type: 'error', message: 'Falha ao salvar SMTP. Verifique as credenciais.' });
        } finally {
            setIsSaving(false);
        }
    };


    const handleTestSmtpConnection = async (testPayloadFromModal) => {
          
        const isPasswordPlaceholder = testPayloadFromModal.SMTP_PASS === "************";
        const smtpPassValue = isPasswordPlaceholder ? "" : testPayloadFromModal.SMTP_PASS;
          
        const testPayload = {
            SMTP_HOST: testPayloadFromModal.SMTP_HOST,
            SMTP_PORT: Number(testPayloadFromModal.SMTP_PORT),
            SMTP_USER: testPayloadFromModal.SMTP_USER,
            SMTP_PASS: smtpPassValue,
            SMTP_SECURE: !!testPayloadFromModal.SMTP_SECURE,
        };

        setNotification({ show: true, type: "info", message: `Testando conexão com ${testPayload.SMTP_HOST}:${testPayload.SMTP_PORT}...` });

        try {
            const res = await api.post('/email/test-smtp', testPayload);
            setNotification({ show: true, type: "success", message: res.data.message });
        } catch (err) {
            const errorMessage = err.response?.data?.message || `Erro de conexão: ${err.message}`;
            setNotification({ show: true, type: "error", message: errorMessage });
        }
    };
      
    const handleDeleteEmailSettings = async () => {
        if (!window.confirm("Tem certeza que deseja DELETAR as configurações SMTP? Isso impedirá o envio de e-mails.")) return;

        setIsSaving(true);
        try {
            const emptyPayload = { SMTP_HOST: "", SMTP_PORT: 587, SMTP_USER: "", SMTP_PASS: "", SMTP_SECURE: false, EMAIL_FROM_NAME: "" };
            await api.put('/email/settings', emptyPayload);

            setSettings(p => ({ ...p, ...emptyPayload }));
            setIsEmailConfigured(false);
            setIsSmtpModalOpen(false); 
            setNotification({ show: true, type: 'success', message: 'Configuração de e-mail excluída com sucesso.' });
        } catch (err) {
            console.error('Erro ao deletar configurações de E-MAIL:', err);
            setNotification({ show: true, type: 'error', message: 'Erro ao deletar configurações de e-mail.' });
        } finally {
            setIsSaving(false);
        }
    };
      

const handleSave = async () => {
    setIsSaving(true);
    let saveSuccess = true;

    try {
        // --- 1. PREPARAR PAYLOAD GLOBAL ---
        const currentZones = settings.FOOTER_ZONES || { zone1: [], zone2: [], zone3: [], zone4: [] };
        const flattened = flattenZones(currentZones);

        const globalPayload = {
            SITE_NAME: settings.SITE_NAME,
            SITE_URL: settings.SITE_URL,
            SITE_LOGO: settings.SITE_LOGO,
            SITE_LOGO_SIZE: Number(settings.SITE_LOGO_SIZE),
            SITE_LOGO_EFFECT: settings.SITE_LOGO_EFFECT,
            SITE_LOGO_SHADOW_COLOR: settings.SITE_LOGO_SHADOW_COLOR,
            SHOW_LOGO_TYPE: settings.SHOW_LOGO_TYPE, 
                      
            HOME_SLIDER_ENABLED: !!settings.HOME_SLIDER_ENABLED,
            MAINTENANCE_MODE: !!settings.MAINTENANCE_MODE,
            
            // Header Styles
            HEADER_BG_COLOR: settings.HEADER_BG_COLOR,
            HEADER_BG_GRADIENT: settings.HEADER_BG_GRADIENT,
            HEADER_EFFECT: settings.HEADER_EFFECT,
            HEADER_TEXT_COLOR: settings.HEADER_TEXT_COLOR,
            HEADER_NEON_COLOR: settings.HEADER_NEON_COLOR,
            
            // Footer Styles
            FOOTER_ENABLED: !!settings.FOOTER_ENABLED,
            FOOTER_MODE: settings.FOOTER_MODE,
            FOOTER_TEXT: settings.FOOTER_TEXT,
            FOOTER_LINKS: settings.FOOTER_LINKS,
            FOOTER_BG_COLOR: settings.FOOTER_BG_COLOR,
            FOOTER_BG_GRADIENT: settings.FOOTER_BG_GRADIENT,
            FOOTER_TEXT_COLOR: settings.FOOTER_TEXT_COLOR,
            FOOTER_EFFECT: settings.FOOTER_EFFECT,
            FOOTER_NEON_COLOR: settings.FOOTER_NEON_COLOR,
            
            FOOTER_WIDGETS: JSON.stringify(flattened.map(({id, ...rest}) => rest) || []),
            FOOTER_ZONES: JSON.stringify(currentZones),
            
            // Contact Button Styles
            CONTACT_BUTTON_ENABLED: !!settings.CONTACT_BUTTON_ENABLED,
            CONTACT_BUTTON_TEXT: settings.CONTACT_BUTTON_TEXT || "Fale Conosco",
            CONTACT_BUTTON_COLOR: settings.CONTACT_BUTTON_COLOR || "#4f46e5",
            CONTACT_BUTTON_ICON: settings.CONTACT_BUTTON_ICON || "",
            CONTACT_BUTTON_LAYOUT: settings.CONTACT_BUTTON_LAYOUT || "pill",
            CONTACT_BUTTON_SIZE: settings.CONTACT_BUTTON_SIZE || "medium",
            CONTACT_BUTTON_POSITION: settings.CONTACT_BUTTON_POSITION || "bottom-right",
            CONTACT_BUTTON_PAGES: settings.CONTACT_BUTTON_PAGES || "*",
            CONTACT_MODAL_CONTENT: settings.CONTACT_MODAL_CONTENT || "[form id=1]",
            
            MENU_ITEMS: settings.MENU_ITEMS,
        };

        // --- 2. EXECUTAR SALVAMENTO (GLOBAL SETTINGS) ---
        await api.put('/settings', globalPayload);
        
        // --- 3. EXECUTAR SALVAMENTO (EFI PAY GATEWAY) ---
        // Verifica se ao menos o Client ID foi mexido para disparar o salvamento da EFI
        if (settings.EFI_CLIENT_ID && settings.EFI_CLIENT_ID !== "") {
            const efiPayload = {
                client_id: settings.EFI_CLIENT_ID,
                client_secret: settings.EFI_CLIENT_SECRET, // O backend ignora se for "************"
                certificate_name: settings.EFI_CERTIFICATE_NAME,
                is_sandbox: settings.EFI_SANDBOX ? 1 : 0
            };
            
            await api.put('/gateway-settings/efi', efiPayload);
        }

        // --- 4. FEEDBACK FINAL ---
        setNotification?.({ 
            show: true, 
            type: 'success', 
            message: 'Todas as configurações foram salvas com sucesso!' 
        });

    } catch (err) {
        console.error('Erro ao salvar configurações:', err);
        const errorMsg = err.response?.data?.message || 'Erro ao salvar configurações.';
        setNotification?.({ show: true, type: 'error', message: errorMsg });
        saveSuccess = false;
    } finally {
        setIsSaving(false);
    }
};

    if (loading) return <div className="p-6 text-center text-indigo-600 font-medium">Carregando configurações...</div>;


// CONTINUAÇÃO DA PARTE 2

    // =========================
    // PART 3 - RENDER + MODALS + FINAL PREVIEW
    // =========================

    const logoFull = getFullLogo(settings.SITE_LOGO);
    // Aplica a classe de efeito do logo
    const logoEffectClass = getLogoEffectClass(settings.SITE_LOGO_EFFECT);

    const getBackgroundStyle = (color, gradient) => {
        if (gradient) return { background: gradient };
        if (color) return { backgroundColor: color };
        return {};
    };
      
    // Obtém os estilos de efeito do header e footer para o preview.
    // ATUALIZADO: Passa a cor neon
    const headerEffectStyle = getEffectStyles(settings.HEADER_EFFECT, settings.HEADER_NEON_COLOR);
    const footerEffectStyle = getEffectStyles(settings.FOOTER_EFFECT, settings.FOOTER_NEON_COLOR);


    const defaultHeaderBgStyle = { background: 'linear-gradient(90deg,#4f46e5,#6366f1)' };
    const defaultFooterBgStyle = { background: '#111827' };

    const headerBgStyle = getBackgroundStyle(settings.HEADER_BG_COLOR, settings.HEADER_BG_GRADIENT);
    const footerBgStyle = getBackgroundStyle(settings.FOOTER_BG_COLOR, settings.FOOTER_BG_GRADIENT);

    // Combina todos os estilos, incluindo os efeitos
    const headerFinalStyle = { 
        ...defaultHeaderBgStyle, 
        ...headerBgStyle, 
        color: settings.HEADER_TEXT_COLOR,
        ...headerEffectStyle,
    };
    const footerFinalStyle = { 
        ...defaultFooterBgStyle, 
        ...footerBgStyle, 
        color: settings.FOOTER_TEXT_COLOR,
        ...footerEffectStyle,
    };

    const TabButton = ({ tabId, icon, label, status }) => (
        <button
            onClick={() => setActiveConnectionTab(tabId)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors duration-200 flex items-center gap-2 ${
                activeConnectionTab === tabId
                    ? "border-indigo-600 text-indigo-700 bg-indigo-50"
                    : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
            }`}
        >
            {icon} {label}
            {status === 'connected' && <span className="w-2 h-2 bg-green-500 rounded-full ml-1" title="Conectado"></span>}
            {status === 'configured' && <span className="w-2 h-2 bg-yellow-500 rounded-full ml-1" title="Configurado (Aguardando Teste/Uso)"></span>}
        </button>
    );

    // FUNÇÃO AUXILIAR PARA PREVIEW DO FOOTER SOCIAL
const renderSocialPreview = (content, textColor) => {
const lines = content.split('\n').filter(line => line.trim());
        
        // Mapeamento de strings para os componentes importados
const socialMap = {
    facebook: <Facebook size={18} />,
    instagram: <Instagram size={18} />,
    twitter: <Twitter size={18} />,
    x: <Twitter size={18} />,
    youtube: <Youtube size={18} />,
    linkedin: <Linkedin size={18} />,
    github: <Github size={18} />,
    web: <Globe size={18} />,
    email: <Mail size={18} />,
    phone: <Phone size={18} />
};

return (
<div className="flex flex-wrap gap-3 mt-2">
{lines.map((line, i) => {
const parts = line.split('|').map(p => p.trim());
                    if (parts.length < 2) return null;

                    const title = parts[0];
                    const url = parts[1];
                    const iconKey = parts[2]?.toLowerCase() || title?.toLowerCase();
                    
                    // Busca o ícone no mapa ou usa o ícone de link como padrão
                    const IconComponent = socialMap[iconKey] || <Globe size={18} />;

return (
<a key={i} 
                           href={url || '#'} 
                           title={title} 
                           target="_blank" 
                           rel="noopener noreferrer"
className="transition-transform hover:scale-110 flex items-center justify-center"
                           style={{ color: textColor }}
>
 {IconComponent}
</a>
);
})}
</div>
);
};


    return (
        <div className="p-4 bg-gray-100 min-h-screen">

            <h2 className="text-3xl font-extrabold text-indigo-600 mb-4">⚙️ Gerenciamento de Configurações Globais</h2>
            <p className="text-gray-600 mb-6">Ajuste as configurações de identidade, comportamento e estilo do seu site.</p>

            {/* GRUPO DE CARDS 1: Identidade, Conexões, Comportamento (GRID DE 3 COLUNAS) */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 mb-8">

                {/* CARD 1 — IDENTIDADE DO SITE (NOME + LOGO) */}
                <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200 h-fit">
                    <h3 className="font-bold text-xl mb-4 text-indigo-600">Identidade e Logo</h3>
                    <label className="block text-sm font-medium mb-1 text-gray-700">Nome do Site</label>
                    <input
                        className="w-full border border-gray-300 rounded-lg p-2 mb-4 focus:ring-indigo-500 focus:border-indigo-500 transition duration-150"
                        value={settings.SITE_NAME || ""}
                        onChange={(e) => update("SITE_NAME", e.target.value)}
                    />

                    <label className="block text-sm font-medium mb-2 text-gray-700">Logo</label>
                    <div className="flex flex-col gap-3">
                        <div className="flex gap-2 items-center flex-wrap">
                            <label className="px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg cursor-pointer hover:bg-indigo-700 transition duration-150 shadow-md">
                                📁 Selecionar Arquivo
                                <input
                                    type="file"
                                    accept="image/*"
                                    className="hidden"
                                    onChange={(e) => handleLogoUpload(e.target.files[0])}
                                />
                            </label>

                            <button
                                className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-100 transition duration-150"
                                onClick={() => openInputModal("SITE_LOGO")}
                            >
                                🔗 Usar URL Externa
                            </button>


                            {settings.SITE_LOGO && (
                                <>
                                    <button
                                        className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-100 transition duration-150"
                                        onClick={() => update("SITE_LOGO", "")}
                                    >
                                        Remover
                                    </button>
                                </>
                            )}
                        </div>

                        {settings.SITE_LOGO ? (
                            <div className="flex flex-col gap-3 mt-3 p-4 border border-indigo-200 rounded-lg bg-indigo-50/50">
                                <div className="flex items-center gap-4">
                                    <div className="p-2 bg-white rounded-md shadow">
                                        <img
                                          src={logoFull}
                                          alt="Preview do Logo"
                                          style={{
                                            height: `${settings.SITE_LOGO_SIZE}px`,
                                            maxHeight: "160px",
                                            maxWidth: "100%",
                                            objectFit: "contain",
                                        
                                            /* ✅ SOMBRA DINÂMICA */
                                            filter:
                                              settings.SITE_LOGO_EFFECT !== "none"
                                                ? `drop-shadow(0 0 16px ${settings.SITE_LOGO_SHADOW_COLOR})`
                                                : "none",
                                          }}
                                          className={`
                                            site-logo
                                            ${logoEffectClass || ""}
                                          `}
                                        />

                                    </div>

                                    <div className="flex-grow">
                                        <label className="block text-sm font-medium mb-2 text-gray-700">Tamanho do Logo: <strong>{settings.SITE_LOGO_SIZE}px</strong></label>
                                        <div className="flex gap-2 items-center">
                                            <input
                                                type="range"
                                                min="20"
                                                max="300"
                                                step="5"
                                                value={settings.SITE_LOGO_SIZE}
                                                onChange={(e) => update("SITE_LOGO_SIZE", Number(e.target.value))}
                                                className="w-full h-2 bg-indigo-200 rounded-lg appearance-none cursor-pointer range-lg"
                                            />
                                        </div>
                                    </div>
                                </div>
                                {/* COR DA SOMBRA / BRILHO */}
                                <label className="block text-sm font-medium mb-1 text-gray-700 mt-3">
                                  Cor da Sombra / Brilho do Logo
                                </label>
                                
                                <div className="flex items-center gap-3">
                                  {/* Color Picker */}
                                  <input
                                    type="color"
                                    value={settings.SITE_LOGO_SHADOW_COLOR || "#000000"}
                                    onChange={(e) =>
                                      update("SITE_LOGO_SHADOW_COLOR", e.target.value)
                                    }
                                    className="h-10 w-14 cursor-pointer rounded border border-gray-300 bg-white"
                                  />
                                
                                  {/* Campo HEX manual */}
                                  <input
                                    type="text"
                                    value={settings.SITE_LOGO_SHADOW_COLOR || "#000000"}
                                    onChange={(e) =>
                                      update("SITE_LOGO_SHADOW_COLOR", e.target.value)
                                    }
                                    className="flex-1 border border-gray-300 rounded-lg p-2 text-sm font-mono"
                                    placeholder="#000000"
                                  />
                                </div>
                                
                                <p className="text-xs text-gray-500 mt-1">
                                  Essa cor será usada na sombra, brilho ou efeito neon do logo.
                                </p>
                                
                                                                  
                                {/* Campo para Efeitos Visuais/Bordas */}
                                <label className="block text-sm font-medium mb-1 text-gray-700 pt-4 border-t">Estilo / Efeito Visual</label>
                                <select
                                    className="w-full border border-gray-300 rounded-lg p-2 bg-white focus:ring-indigo-500 focus:border-indigo-500"
                                    value={settings.SITE_LOGO_EFFECT}
                                    onChange={(e) => update("SITE_LOGO_EFFECT", e.target.value)}
                                >
                                    <option value="none">Nenhum</option>
                                    <option value="round-shadow">Borda Redonda + Sombra</option>
                                    <option value="neon-glow">Brilho Neon</option>
                                    <option value="border-glow">Borda Amarela Destacada</option>
                                    <option value="grayscale">Escala de Cinza (Grayscale)</option>
                                </select>
                                  
                                <input
                                    type="number"
                                    min="20"
                                    max="300"
                                    value={settings.SITE_LOGO_SIZE}
                                    onChange={(e) => update("SITE_LOGO_SIZE", Number(e.target.value))}
                                    className="w-full border border-gray-300 rounded-lg p-1 text-center text-sm"
                                />

                            </div>
                        ) : (
                            <div className="text-sm text-gray-500 p-4 border border-gray-300 rounded-lg text-center bg-gray-50">Nenhum logo carregado.</div>
                        )}

                        <div className="mt-4 pt-4 border-t border-gray-200">
                            <label className="block text-sm font-medium mb-1 text-gray-700">Exibir</label>
                            <select
                                className="w-full border border-gray-300 rounded-lg p-2 bg-white focus:ring-indigo-500 focus:border-indigo-500"
                                value={settings.SHOW_LOGO_TYPE}
                                onChange={(e) => update("SHOW_LOGO_TYPE", e.target.value)}
                            >
                                <option value="logo_only">Somente logo</option>
                                <option value="name_only">Somente nome</option>
                                <option value="logo_and_name">Logo + nome</option>
                            </select>
                        </div>
                    </div>
                </div>

                {/* CARD 2 — SERVIÇOS DE CONEXÃO E E-MAIL (UNIFICADO) */}
                <div className="bg-white rounded-xl shadow-lg border border-gray-200 h-fit">
                    <div className="p-6">
                        <h3 className="font-bold text-xl mb-4 text-indigo-600">🔌 Serviços de Conexão e E-mail</h3>
                        <p className="text-sm text-gray-500 mb-4">Escolha e configure os serviços que o sistema usará para comunicação.</p>
                    </div>

                    {/* ABAS */}
                    <div className="flex border-b border-gray-200 bg-gray-50/70 sticky top-0">
                        <TabButton
                            tabId="smtp"
                            icon="📧"
                            label="E-mail (SMTP)"
                            status={isEmailConfigured ? 'connected' : 'none'}
                        />
                        <TabButton
                            tabId="google"
                            icon="🔑"
                            label="Google OAuth"
                            status={isGoogleConnected ? 'connected' : 'none'}
                        />
                        <TabButton
                            tabId="outros"
                            icon="🌐"
                            label="Outras APIs"
                            status="none"
                        />
                    </div>

                    <div className="p-6">
                        {/* CONTEÚDO DA ABA SMTP */}
                        {activeConnectionTab === 'smtp' && (
                            <>
                                <h4 className="font-bold text-lg text-indigo-700 mb-4">Configurações de E-mail (SMTP)</h4>
                                  
                                {isEmailConfigured ? (
                                    <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700 space-y-2">
                                        <p className="font-bold">✅ E-mail SMTP Configurado e Ativo</p>
                                        <p>Enviando como: <strong>{settings.EMAIL_FROM_NAME || settings.SMTP_USER}</strong></p>
                                        <p>Servidor: <span className="font-mono bg-gray-100 p-0.5 rounded text-xs text-gray-800">{settings.SMTP_HOST}:{settings.SMTP_PORT}</span></p>
                                        <div className="pt-2">
                                            <button
                                                onClick={() => setIsSmtpModalOpen(true)}
                                                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition font-bold text-sm"
                                            >
                                                ⚙️ Editar Credenciais / Testar
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="text-center p-8 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                                        <p className="text-gray-600 mb-4">Nenhuma conta SMTP configurada.</p>
                                        <button
                                            onClick={() => setIsSmtpModalOpen(true)}
                                            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition font-bold"
                                        >
                                            Inserir Credenciais SMTP
                                        </button>
                                    </div>
                                )}
                            </>
                        )}

                        {/* CONTEÚDO DA ABA GOOGLE OAUTH */}
                        {activeConnectionTab === 'google' && (
                            <>
                                <h4 className="font-bold text-lg text-indigo-700 mb-4">Google OAuth</h4>
                                <p className="text-sm text-gray-500 mb-4">Conecte sua conta do Google para permitir integrações de serviços.</p>

                                {isGoogleCheckingStatus ? (
                                    <div className="text-center p-8 bg-gray-50 rounded-lg border border-dashed border-gray-300 text-gray-600">
                                        Verificando status de conexão...
                                    </div>
                                ) : isGoogleConnected ? (
                                    <div className="space-y-4">
                                        <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-700 flex items-center gap-3">
                                            {googleInfo.avatar && <img src={googleInfo.avatar} alt="Avatar" className="w-8 h-8 rounded-full" />}
                                            <div>
                                                Conectado como: <strong>{googleInfo.name || googleInfo.email}</strong>.
                                                <div className="text-xs text-blue-600">Desde: {new Date(googleInfo.connected_at).toLocaleDateString()}</div>
                                            </div>
                                        </div>
                                        <button
                                            onClick={handleGoogleDisconnect}
                                            className="w-full px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition font-bold text-sm"
                                        >
                                            ❌ Desconectar Conta do Google
                                        </button>
                                        <button
                                            onClick={handleTestGoogleApi}
                                            className="w-full px-4 py-2 text-sm font-bold bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition"
                                        >
                                            ⚙️ Testar API Conectada
                                        </button>
                                    </div>
                                ) : (
                                    <div className="text-center p-8 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                                        <p className="text-gray-600 mb-4">Nenhuma conta do Google conectada.</p>
                                        <button
                                            onClick={handleGoogleConnect}
                                            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition font-bold"
                                        >
                                            🔗 Conectar com Google
                                        </button>
                                    </div>
                                )}
                            </>
                        )}

                        {/* CONTEÚDO DA ABA OUTRAS APIS */}
                        {activeConnectionTab === 'outros' && (
                            <div className="text-center p-8 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                                <h4 className="font-bold text-lg text-indigo-700 mb-2">Futuras Integrações</h4>
                                <p className="text-gray-600 mb-4">Esta aba está reservada para futuras conexões de API (ex: Slack, Microsoft, etc.).</p>
                                <button className="px-4 py-2 border rounded-lg text-sm text-gray-700 bg-white hover:bg-gray-100 transition">Sugerir Conexão</button>
                            </div>
                        )}
                    </div>
                </div>
                  
                {/* CARD 3 — COMPORTAMENTO + BOTÃO FLUTUANTE */}
                <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200 h-fit">
                    <h3 className="font-bold text-xl mb-4 text-indigo-600">Comportamento do Site</h3>
                    <div className="space-y-4">
                        <div>
                            <Toggle checked={!!settings.HOME_SLIDER_ENABLED} onChange={(v) => update("HOME_SLIDER_ENABLED", v)} label="Ativar Slider na Home" />
                            <p className="text-xs text-gray-500 -mt-1">Se ativo, mostra o slider na página inicial.</p>
                        </div>

                        <div>
                            <Toggle checked={!!settings.MAINTENANCE_MODE} onChange={(v) => update("MAINTENANCE_MODE", v)} label="Modo Manutenção" />
                            <p className="text-xs text-red-500 -mt-1 border-b pb-4">**Atenção:** Usuários não logados verão a página de manutenção.</p>
                        </div>
                    </div>

                    <h3 className="font-bold text-xl mb-4 pt-4 border-t border-gray-200 mt-4 text-indigo-600">Botão de Contato Flutuante</h3>

                    <div className="space-y-3">
                        <Toggle checked={!!settings.CONTACT_BUTTON_ENABLED} onChange={(v) => update("CONTACT_BUTTON_ENABLED", v)} label="Ativar Botão Flutuante" />

                        {settings.CONTACT_BUTTON_ENABLED && (
                            <>
                                <label className="text-sm font-medium block text-gray-700">Texto do Botão</label>
                                <input className="border border-gray-300 p-2 rounded-lg w-full" value={settings.CONTACT_BUTTON_TEXT} onChange={(e) => update("CONTACT_BUTTON_TEXT", e.target.value)} />

                                <label className="text-sm font-medium mt-2 block text-gray-700">Cor do Botão</label>
                                <input type="color" className="border border-gray-300 p-1 rounded-lg w-full h-10 cursor-pointer" value={settings.CONTACT_BUTTON_COLOR} onChange={(e) => update("CONTACT_BUTTON_COLOR", e.target.value)} />

                                <label className="text-sm font-medium mt-3 block text-gray-700">Ícone (opcional)</label>
                                <div className="flex gap-2 items-center flex-wrap">
                                    <label className="px-3 py-1.5 bg-indigo-500 text-white rounded-lg text-sm cursor-pointer hover:bg-indigo-600 transition">Upload
                                        <input type="file" accept="image/*" className="hidden" onChange={handleContactIconUpload} />
                                    </label>

                                    <button className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-100 transition" onClick={() => openInputModal("CONTACT_BUTTON_ICON")}>🔗 Usar URL</button>

                                    {settings.CONTACT_BUTTON_ICON && (<>
                                        <button className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-100 transition" onClick={() => update("CONTACT_BUTTON_ICON", "")}>Remover</button>
                                        <img src={getFullLogo(settings.CONTACT_BUTTON_ICON)} alt="icon" className="w-6 h-6 object-contain border rounded-full" />
                                    </>)}
                                </div>

                                <label className="text-sm font-medium mt-4 block text-gray-700">Layout</label>
                                <select className="border border-gray-300 p-2 rounded-lg w-full bg-white" value={settings.CONTACT_BUTTON_LAYOUT} onChange={(e) => update("CONTACT_BUTTON_LAYOUT", e.target.value)}>
                                    <option value="pill">Pílula</option>
                                    <option value="round">Redondo</option>
                                    <option value="square">Quadrado</option>
                                    <option value="float">Flutuante 3D</option>
                                </select>

                                <label className="text-sm font-medium mt-3 block text-gray-700">Tamanho</label>
                                <select className="border border-gray-300 p-2 rounded-lg w-full bg-white" value={settings.CONTACT_BUTTON_SIZE} onChange={(e) => update("CONTACT_BUTTON_SIZE", e.target.value)}>
                                    <option value="small">Pequeno</option>
                                    <option value="medium">Médio</option>
                                    <option value="large">Grande</option>
                                </select>

                                <label className="text-sm font-medium mt-3 block text-gray-700">Posição da Tela</label>
                                <select className="border border-gray-300 p-2 rounded-lg w-full bg-white" value={settings.CONTACT_BUTTON_POSITION} onChange={(e) => update("CONTACT_BUTTON_POSITION", e.target.value)}>
                                    <option value="bottom-right">Inferior Direita</option>
                                    <option value="bottom-left">Inferior Esquerda</option>
                                    <option value="top-right">Superior Direita</option>
                                    <option value="top-left">Superior Esquerda</option>
                                </select>

                                <label className="text-sm font-medium mt-4 block text-gray-700">Páginas onde aparece</label>
                                <RichTextEditor rows={4} value={settings.CONTACT_BUTTON_PAGES} onChange={(e) => update("CONTACT_BUTTON_PAGES", e.target.value)} placeholder={`Exemplos:\n*\n!/admin\n/cursos/*`} />

                                <label className="text-sm font-medium mt-4 block text-gray-700">Conteúdo do Modal (HTML ou shortcode)</label>
                                <RichTextEditor rows={4} value={settings.CONTACT_MODAL_CONTENT} onChange={(e) => update("CONTACT_MODAL_CONTENT", e.target.value)} />

                                {/* PREVIEW DO BOTÃO FLUTUANTE */}
                                <div className="p-4 border rounded-lg mt-4 text-center shadow-inner" style={{ backgroundColor: settings.CONTACT_BUTTON_COLOR + "15" }}>
                                    <div 
                                        style={{ 
                                            display: 'inline-flex', 
                                            alignItems: 'center', 
                                            gap: '10px', 
                                            padding: settings.CONTACT_BUTTON_SIZE === 'small' ? '8px 16px' : settings.CONTACT_BUTTON_SIZE === 'large' ? '16px 28px' : '12px 22px', 
                                            backgroundColor: settings.CONTACT_BUTTON_COLOR, 
                                            borderRadius: settings.CONTACT_BUTTON_LAYOUT === 'pill' ? '50px' : settings.CONTACT_BUTTON_LAYOUT === 'round' ? '100%' : settings.CONTACT_BUTTON_LAYOUT === 'square' ? '6px' : settings.CONTACT_BUTTON_LAYOUT === 'float' ? '12px' : '12px', 
                                            boxShadow: settings.CONTACT_BUTTON_LAYOUT === 'float' ? `0 6px 0 ${settings.CONTACT_BUTTON_COLOR.replace(/#/, '#1c1555')}` : "0 4px 12px rgba(0,0,0,0.4)",
                                            color: "#fff",
                                            cursor: "pointer",
                                            fontWeight: "bold",
                                            textTransform: "uppercase",
                                            border: "none",
                                            transition: 'background-color 0.3s ease, box-shadow 0.3s ease, transform 0.1s ease',
                                            fontSize: settings.CONTACT_BUTTON_SIZE === "small" ? "0.75rem" : settings.CONTACT_BUTTON_SIZE === "large" ? "1rem" : "0.875rem",
                                        }}
                                        onClick={(e) => { e.preventDefault(); openMiniModal("Simulação de Modal", `Abrir modal com conteúdo:\n\n${settings.CONTACT_MODAL_CONTENT}`); }}
                                    >
                                        {settings.CONTACT_BUTTON_ICON && (<img src={getFullLogo(settings.CONTACT_BUTTON_ICON)} alt="icon" className="w-5 h-5 object-contain" />)}
                                        {settings.CONTACT_BUTTON_TEXT || 'Fale Conosco'}
                                    </div>
                                </div>

                            </>
                        )}

                    </div>
                </div>

            </div>
              
            {/* GRUPO DE CARDS 2: CONTROLE DE CERTIFICADO + Estilo Header e Footer (GRID DE 2 COLUNAS) */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">

                {/* NOVO CARD: Controle de Certificado */}
                <CertificateControlCard setNotification={setNotification} />

                {/* CARD 4 — ESTILO DO CABEÇALHO */}
                <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200 h-fit">
                    <h3 className="font-bold text-xl mb-4 text-indigo-600">Estilo do Cabeçalho</h3>

                    <div className="space-y-4">
                    <label className="block text-sm font-medium mb-1 text-gray-700">Cor de Fundo (Hex ou Nome)</label>
                    <input type="text" className="border border-gray-300 p-2 rounded-lg w-full" placeholder="#000000" value={settings.HEADER_BG_COLOR || ''} onChange={(e) => update('HEADER_BG_COLOR', e.target.value)} />

                    <label className="block text-sm font-medium mb-1 text-gray-700">Gradiente de Fundo (CSS)</label>
                    <input type="text" className="border border-gray-300 p-2 rounded-lg w-full" placeholder="linear-gradient(...)" value={settings.HEADER_BG_GRADIENT || ''} onChange={(e) => update('HEADER_BG_GRADIENT', e.target.value)} />

                    <label className="block text-sm font-medium mb-1 text-gray-700">Efeito Visual</label>
                    <select
                    className="w-full border border-gray-300 rounded-lg p-2 bg-white focus:ring-indigo-500 focus:border-indigo-500"
                    value={settings.HEADER_EFFECT}
                    onChange={(e) => update('HEADER_EFFECT', e.target.value)}
                    >
                    <option value="none">Nenhum</option>
                    <option value="rain">Chuva</option>
                    <option value="stars">Estrelas</option>
                    <option value="neon">Neon</option>
                    <option value="glass">Glass (Transparente)</option>
                    <option value="dots">Pontos</option> 
                    <option value="wave">Onda</option> 
                    </select>
                      
                    {/* NOVO CAMPO DE COR NEON PARA CABEÇALHO */}
                    {settings.HEADER_EFFECT === 'neon' && (
                        <>
                            <label className="block text-sm font-medium mb-1 text-gray-700">Cor do Neon (Cabeçalho)</label>
                            <input type="color" className="border border-gray-300 p-2 rounded-lg w-full h-10" value={settings.HEADER_NEON_COLOR} onChange={(e) => update('HEADER_NEON_COLOR', e.target.value)} />
                        </>
                    )}
                    {/* FIM NOVO CAMPO DE COR NEON */}

                    <label className="block text-sm font-medium mb-1 text-gray-700">Cor do Texto do Cabeçalho</label>
                    <input type="color" className="border border-gray-300 p-2 rounded-lg w-full h-10" value={settings.HEADER_TEXT_COLOR} onChange={(e) => update('HEADER_TEXT_COLOR', e.target.value)} />

                    <button onClick={() => { update('HEADER_BG_COLOR', ''); update('HEADER_BG_GRADIENT', ''); update('HEADER_EFFECT', 'none'); update('HEADER_NEON_COLOR', '#ff00ff'); }} className="w-full px-4 py-2 border rounded hover:bg-gray-100 transition">Limpar Estilos</button>
                    </div>

                    <h4 className="font-semibold mt-6 mb-2">Preview do Cabeçalho</h4>
                    <div className="mt-4 rounded-lg overflow-hidden shadow-lg border" style={{ minHeight: '64px', display: 'flex', alignItems: 'center', ...headerFinalStyle }}>
                    <div className="p-3 w-full flex items-center justify-between">
                    <div className="flex items-center gap-3">
                    {(settings.SHOW_LOGO_TYPE !== 'name_only') && settings.SITE_LOGO && (<img src={logoFull} alt="logo" style={{ height: 40, objectFit: 'contain' }} className={`rounded ${logoEffectClass}`} />)}
                    {(settings.SHOW_LOGO_TYPE !== 'logo_only') && (<div style={{ color: settings.HEADER_TEXT_COLOR }} className="font-extrabold text-xl">{settings.SITE_NAME || 'Meu Site'}</div>)}
                    </div>

                    <div className="hidden md:flex gap-4 text-sm font-medium" style={{ color: settings.HEADER_TEXT_COLOR }}>
                    <div>Login</div>
                    <div>Cadastre-se</div>
                    </div>
                    </div>
                    </div>
                </div>
            </div>

            {/* GRUPO DE CARDS 3: Estilo Rodapé (Mantido, mas em uma nova linha) */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                {/* CARD 5 — ESTILO DO RODAPÉ (FOOTER STYLES) */}
                <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200 h-fit">
                    <h3 className="font-bold text-xl mb-4 text-indigo-600">🎨 Estilo do Rodapé (Footer)</h3>

                    <div className="space-y-4">
                    <Toggle checked={!!settings.FOOTER_ENABLED} onChange={(v) => update("FOOTER_ENABLED", v)} label="Ativar Rodapé" />
                    <p className="text-xs text-gray-500 -mt-1 border-b pb-4">Define se o rodapé será exibido.</p>

                    <label className="block text-sm font-medium mb-1 text-gray-700">Modo de Exibição</label>
                    <select
                    className="w-full border border-gray-300 rounded-lg p-2 bg-white focus:ring-indigo-500 focus:border-indigo-500"
                    value={settings.FOOTER_MODE}
                    onChange={(e) => update('FOOTER_MODE', e.target.value)}
                    >
                    <option value="simple">Simples (Texto e Links)</option>
                    <option value="builder">Builder (Widgets Dinâmicos)</option>
                    </select>

                    <label className="block text-sm font-medium mb-1 text-gray-700 pt-4 border-t">Cor de Fundo (Hex ou Nome)</label>
                    <input type="text" className="border border-gray-300 p-2 rounded-lg w-full" placeholder="#111827" value={settings.FOOTER_BG_COLOR || ''} onChange={(e) => update('FOOTER_BG_COLOR', e.target.value)} />

                    <label className="block text-sm font-medium mb-1 text-gray-700">Gradiente de Fundo (CSS)</label>
                    <input type="text" className="border border-gray-300 p-2 rounded-lg w-full" placeholder="linear-gradient(...)" value={settings.FOOTER_BG_GRADIENT || ''} onChange={(e) => update('FOOTER_BG_GRADIENT', e.target.value)} />

                    <label className="block text-sm font-medium mb-1 text-gray-700">Efeito Visual</label>
                    <select
                    className="w-full border border-gray-300 rounded-lg p-2 bg-white focus:ring-indigo-500 focus:border-indigo-500"
                    value={settings.FOOTER_EFFECT}
                    onChange={(e) => update('FOOTER_EFFECT', e.target.value)}
                    >
                    <option value="none">Nenhum</option>
                    <option value="rain">Chuva</option>
                    <option value="stars">Estrelas</option>
                    <option value="neon">Neon</option>
                    <option value="glass">Glass (Transparente)</option>
                    <option value="dots">Pontos</option> 
                    <option value="wave">Onda</option> 
                    </select>
                      
                    {/* NOVO CAMPO DE COR NEON PARA RODAPÉ */}
                    {(settings.FOOTER_EFFECT === 'neon' || settings.FOOTER_EFFECT === 'dots' || settings.FOOTER_EFFECT === 'wave') && (
                        <>
                            <label className="block text-sm font-medium mb-1 text-gray-700">Cor do Efeito (Neon/Pontos/Onda)</label>
                            <input type="color" className="border border-gray-300 p-2 rounded-lg w-full h-10" value={settings.FOOTER_NEON_COLOR} onChange={(e) => update('FOOTER_NEON_COLOR', e.target.value)} />
                        </>
                    )}
                    {/* FIM NOVO CAMPO DE COR NEON */}

                    <label className="block text-sm font-medium mb-1 text-gray-700">Cor do Texto</label>
                    <input type="color" className="border border-gray-300 p-2 rounded-lg w-full h-10" value={settings.FOOTER_TEXT_COLOR} onChange={(e) => update('FOOTER_TEXT_COLOR', e.target.value)} />

                    <button onClick={() => { update('FOOTER_BG_COLOR', ''); update('FOOTER_BG_GRADIENT', ''); update('FOOTER_EFFECT', 'none'); update('FOOTER_TEXT_COLOR', '#ffffff'); update('FOOTER_NEON_COLOR', '#ff00ff'); }} className="w-full px-4 py-2 border rounded hover:bg-gray-100 transition text-red-600">🧹 Limpar Estilos do Rodapé</button>

                    <label className="block text-sm font-medium mb-1 text-gray-700 pt-4 border-t">Texto de Copyright / Rodapé Simples</label>
                    <RichTextEditor
                    rows={3}
                    value={settings.FOOTER_TEXT}
                    onChange={(e) => update("FOOTER_TEXT", e.target.value)}
                    placeholder={`© ${new Date().getFullYear()} ${settings.SITE_NAME || 'Meu Site'}`}
                    />

                    {settings.FOOTER_MODE === 'simple' && (
                    <>
                    <label className="block text-sm font-medium mb-1 text-gray-700">Links do Rodapé Simples (formato: Texto | URL)</label>
                    <RichTextEditor
                        rows={3}
                        value={settings.FOOTER_LINKS}
                        onChange={(e) => update("FOOTER_LINKS", e.target.value)}
                        placeholder="Ex: Política | /url-politica\nTermos | /url-termos"
                    />
                    </>
                    )}

                    </div>
                </div>
                {/* Adicionando uma coluna vazia aqui para manter o layout se o CertificateControlCard estiver do lado oposto */}
                <div className="hidden md:block"></div> 
            </div>


{/* CARD EFI PAY - Adicione no seu grid de cards */}
<div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200 h-fit">
    <h3 className="font-bold text-xl mb-4 text-orange-600 flex items-center gap-2">
        <span>💳 Configuração EFI Pay (PIX/Boleto)</span>
    </h3>
    
    <div className="space-y-4">
        <div>
            <label className="block text-sm font-medium text-gray-700">Client ID</label>
            <input 
                type="text" 
                className="w-full border border-gray-300 rounded-lg p-2 text-sm font-mono"
                value={settings.EFI_CLIENT_ID}
                onChange={(e) => update("EFI_CLIENT_ID", e.target.value)}
            />
        </div>

        <div>
            <label className="block text-sm font-medium text-gray-700">Client Secret</label>
            <input 
                type="password" 
                className="w-full border border-gray-300 rounded-lg p-2 text-sm font-mono"
                value={settings.EFI_CLIENT_SECRET}
                onChange={(e) => update("EFI_CLIENT_SECRET", e.target.value)}
            />
        </div>

        <div>
            <label className="block text-sm font-medium text-gray-700">Nome do Certificado (.p12)</label>
            <input 
                type="text" 
                className="w-full border border-gray-300 rounded-lg p-2 text-sm"
                placeholder="ex: producao-Lanches.p12"
                value={settings.EFI_CERTIFICATE_NAME}
                onChange={(e) => update("EFI_CERTIFICATE_NAME", e.target.value)}
            />
            <p className="text-[10px] text-gray-500 mt-1">O arquivo deve estar na pasta /certs do servidor.</p>
        </div>

        <Toggle 
            checked={settings.EFI_SANDBOX} 
            onChange={(v) => update("EFI_SANDBOX", v)} 
            label="Modo Homologação (Sandbox)" 
        />
    </div>
</div>




            {/* FOOTER BUILDER — 4 ZONES */}
{settings.FOOTER_MODE === 'builder' && (
    <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200 mt-6 h-fit">
        <h3 className="font-bold text-2xl mb-4 text-indigo-700">🏗️ Footer Builder - Widgets Dinâmicos</h3>
        <p className="text-gray-600 mb-6 italic text-sm">
            Formato Social: <code className="bg-gray-100 px-1">Nome | URL | ícone</code> (Ícones: instagram, facebook, youtube, linkedin, x, github)
        </p>

        <div className="flex gap-2 flex-wrap mb-6 border-b pb-4 border-gray-200">
            <button onClick={() => addWidget('text','zone1')} className="px-3 py-1 bg-indigo-600 text-white rounded hover:bg-indigo-700 transition">+ Texto</button>
            <button onClick={() => addWidget('links','zone1')} className="px-3 py-1 border rounded hover:bg-gray-100 transition">+ Links</button>
            <button onClick={() => addWidget('address','zone1')} className="px-3 py-1 border rounded hover:bg-gray-100 transition">+ Endereço</button>
            <button onClick={() => addWidget('social','zone1')} className="px-3 py-1 border border-indigo-300 text-indigo-600 rounded hover:bg-indigo-50 transition">+ Social (Ícones)</button>
            <button onClick={() => addWidget('map','zone1')} className="px-3 py-1 border rounded hover:bg-gray-100 transition">+ Mapa</button>
            <button onClick={() => addWidget('html','zone1')} className="px-3 py-1 border rounded hover:bg-gray-100 transition">+ HTML</button>
        </div>

        <DragDropContext onDragEnd={onDragEnd}>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {['zone1','zone2','zone3','zone4'].map((zoneId, zoneIndex) => (
                    <Droppable droppableId={zoneId} key={zoneId}>
                        {(provided) => (
                            <div ref={provided.innerRef} {...provided.droppableProps} className="p-3 bg-gray-50 rounded border min-h-[200px]">
                                <div className="font-semibold mb-2 text-gray-400 text-xs uppercase">Coluna {zoneIndex+1}</div>

                                {(settings.FOOTER_ZONES?.[zoneId] || []).map((w, idx) => (
                                    <Draggable key={w.id} draggableId={w.id} index={idx}>
                                        {(prov) => (
                                            <div ref={prov.innerRef} {...prov.draggableProps} {...prov.dragHandleProps} className="mb-3 p-3 bg-white rounded shadow-sm border border-gray-200">
                                                <div className="flex justify-between items-start mb-2">
                                                    <span className="text-[10px] font-bold uppercase text-indigo-500 bg-indigo-50 px-2 py-0.5 rounded">{w.type}</span>
                                                    <div className="flex gap-1">
                                                        <button className="text-[10px] p-1 border rounded" onClick={() => removeWidget(zoneId, idx)}>🗑️</button>
                                                    </div>
                                                </div>

                                                <input 
                                                    className="w-full border rounded p-1 text-sm mb-2 font-bold" 
                                                    value={w.title} 
                                                    onChange={(e) => updateWidget(zoneId, idx, { title: e.target.value })} 
                                                    placeholder="Título da Coluna" 
                                                />

                                                <textarea 
                                                    className="w-full border rounded p-2 text-xs font-mono" 
                                                    rows={4}
                                                    value={w.content}
                                                    onChange={(e) => updateWidget(zoneId, idx, { content: e.target.value })}
                                                    placeholder={
                                                        w.type === 'social' ? "Instagram | https://inst... | instagram" : 
                                                        w.type === 'links' ? "Contato | /contato" : "Conteúdo..."
                                                    }
                                                />
                                            </div>
                                        )}
                                    </Draggable>
                                ))}
                                {provided.placeholder}
                            </div>
                        )}
                    </Droppable>
                ))}
            </div>
        </DragDropContext>
    </div>
)}

{/* PREVIEW DO RODAPÉ */}
<div className="mt-8 border-t-4 border-indigo-100 pt-6">
    <h4 className="font-bold text-lg mb-4 flex items-center gap-2">👁️ Preview Real do Rodapé</h4>
    {settings.FOOTER_ENABLED && (
        <div
            className="rounded-xl shadow-2xl p-8 transition-all duration-500"
            style={{ ...footerFinalStyle }}
        >
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8" style={{ color: settings.FOOTER_TEXT_COLOR }}>
                {['zone1','zone2','zone3','zone4'].map((z) => (
                    <div key={z} className="space-y-4">
                        {(settings.FOOTER_ZONES?.[z] || []).filter(w => w.enabled).map((w, wi) => (
                            <div key={wi} className="animate-fadeIn">
                                {w.title && <div className="font-bold text-sm mb-3 border-b border-white/20 pb-1 inline-block">{w.title}</div>}
                                
                                {w.type === 'social' ? (
                                    renderSocialPreview(w.content, settings.FOOTER_TEXT_COLOR)
                                ) : w.type === 'links' ? (
                                    <div className="flex flex-col gap-2">
                                        {w.content.split('\n').map((ln, a) => {
                                            const parts = ln.split('|').map(p => p.trim());
                                            return parts.length >= 2 ? (
                                                <a key={a} href={parts[1]} className="text-xs hover:translate-x-1 transition-transform opacity-90 hover:opacity-100">
                                                    {parts[0]}
                                                </a>
                                            ) : null;
                                        })}
                                    </div>
                                ) : w.type === 'html' ? (
                                    <div dangerouslySetInnerHTML={{ __html: w.content }} className="text-xs opacity-90" />
                                ) : (
                                    <div className="text-xs opacity-90 whitespace-pre-line leading-relaxed">
                                        {w.content}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                ))}
            </div>
        </div>
    )}
</div>

            <div className="flex justify-end mt-8 py-4 bg-white sticky bottom-0 border-t border-gray-200 shadow-lg -mx-6 px-6">
                <button onClick={handleSave} disabled={isSaving} className="px-8 py-3 bg-green-600 text-white font-bold rounded-lg hover:bg-green-700 transition">{isSaving ? '💾 Salvando...' : '✅ Salvar Configurações'}</button>
            </div>

            {/* SIMULAÇÃO DO BOTÃO FLUTUANTE (PREVIEW FINAL) - MANTIDO INALTERADO */}
            {settings.CONTACT_BUTTON_ENABLED && (
                <div className={`fixed z-50 transition-all duration-500 ease-out ${settings.CONTACT_BUTTON_POSITION === "bottom-right" ? "bottom-8 right-8" : settings.CONTACT_BUTTON_POSITION === "bottom-left" ? "bottom-8 left-8" : settings.CONTACT_BUTTON_POSITION === "top-right" ? "top-8 right-8" : "top-8 left-8"}`}>
                    <button
                        title="Botão de Contato (Simulação)"
                        style={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: "10px",
                            padding: settings.CONTACT_BUTTON_SIZE === "small" ? "8px 16px" : settings.CONTACT_BUTTON_SIZE === "large" ? "16px 28px" : "12px 22px",
                            backgroundColor: settings.CONTACT_BUTTON_COLOR,
                            borderRadius: settings.CONTACT_BUTTON_LAYOUT === "pill" ? "50px" : settings.CONTACT_BUTTON_LAYOUT === "round" ? "100%" : settings.CONTACT_BUTTON_LAYOUT === "square" ? "6px" : "12px",
                            boxShadow: settings.CONTACT_BUTTON_LAYOUT === "float" ? '0 8px 0 ' + settings.CONTACT_BUTTON_COLOR.replace(/#/, '#1c1555') : "0 4px 12px rgba(0,0,0,0.4)",
                            color: "#fff",
                            cursor: "pointer",
                            fontWeight: "bold",
                            textTransform: "uppercase",
                            border: "none",
                            transition: 'background-color 0.3s ease, box-shadow 0.3s ease, transform 0.1s ease',
                            fontSize: settings.CONTACT_BUTTON_SIZE === "small" ? "0.75rem" : settings.CONTACT_BUTTON_SIZE === "large" ? "1rem" : "0.875rem",
                        }}
                        onClick={(e) => { e.preventDefault(); openMiniModal("Simulação de Modal", `Abrir modal com conteúdo:\n\n${settings.CONTACT_MODAL_CONTENT}`); }}
                    >
                        {settings.CONTACT_BUTTON_ICON && (<img src={getFullLogo(settings.CONTACT_BUTTON_ICON)} alt="icon" className="w-5 h-5 object-contain" />)}
                        {settings.CONTACT_BUTTON_TEXT}
                    </button>
                </div>
            )}
              
            {/* Renderiza os Modais */}
            <MiniModal show={miniModal.show} title={miniModal.title} message={miniModal.message} />
            <InputModal 
                show={inputModal.show} 
                keyName={inputModal.key} 
                placeholder={inputModal.placeholder} 
                onSubmit={inputModal.onSubmit} 
                onClose={closeInputModal}
            />
              
            {/* NOVO MODAL SMTP */}
            <SmtpSettingsModal
                show={isSmtpModalOpen}
                settings={settings}
                onSave={handleSaveEmailSettings}
                onTest={handleTestSmtpConnection}
                onDelete={handleDeleteEmailSettings}
                onClose={() => {
                    // Ao fechar, recarrega as settings para limpar qualquer input local de senha no estado principal
                    loadEmailSettings().then(data => { setSettings(p => ({ ...p, ...data })); });
                    setIsSmtpModalOpen(false);
                }}
                isSaving={isSaving}
            />
        </div>
    );
}

export default SettingsManagement;
 